// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useState, useEffect, useRef } from 'react';
import { searchClubs, type Club } from '../api/directory';
import logger from '../utils/logger';

interface ClubSelectorProps {
  value: string;
  onChange: (clubId: string) => void;
  error?: string;
}

export default function ClubSelector({ value, onChange, error }: ClubSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load selected club details if value is set
  useEffect(() => {
    if (value && !selectedClub) {
      // If we have a value but no selected club, we could fetch it
      // For now, we'll just track it via the value prop
      // The parent component should handle displaying the selected club name
    }
  }, [value, selectedClub]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length < 2) {
      setClubs([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await searchClubs({ q: searchQuery, limit: 10 });
        setClubs(response.data.clubs || []);
        setShowResults(true);
      } catch (err) {
        logger.error('Failed to search clubs:', err);
        setClubs([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectClub = (club: Club) => {
    setSelectedClub(club);
    setSearchQuery(club.name);
    setShowResults(false);
    onChange(club.id);
  };

  const handleClear = () => {
    setSelectedClub(null);
    setSearchQuery('');
    setClubs([]);
    setShowResults(false);
    onChange('');
  };

  return (
    <div ref={containerRef} className="relative" data-testid="club-selector">
      <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="club-selector">
        Club *
      </label>

      <div className="relative">
        <input
          id="club-selector"
          type="text"
          data-testid="club-selector-input"
          className={`w-full rounded-lg border ${
            error ? 'border-red-300' : 'border-slate-200'
          } px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100`}
          placeholder="Search for your club..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (selectedClub || value) {
              setSelectedClub(null);
              onChange('');
            }
          }}
          onFocus={() => {
            if (searchQuery.length >= 2 && clubs.length > 0) {
              setShowResults(true);
            } else if (searchQuery.length > 0 && searchQuery.length < 2) {
              setShowResults(true);
            }
          }}
          autoComplete="off"
          required
        />
        
        {selectedClub && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600"
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
      </div>

      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}

      {showResults && (
        <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-slate-500">Searching...</div>
          ) : clubs.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500">
              {searchQuery.length < 2 
                ? 'Type at least 2 characters to search'
                : 'No clubs found'}
            </div>
          ) : (
            <ul className="py-1" data-testid="club-selector-results">
              {clubs.map((club) => (
                <li key={club.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectClub(club)}
                    data-testid={`club-option-${club.id}`}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                  >
                    <div className="font-medium text-slate-900">{club.name}</div>
                    {club.description && (
                      <div className="text-xs text-slate-500 truncate">
                        {club.description}
                      </div>
                    )}
                    {club.country && (
                      <div className="text-xs text-slate-500">{club.country}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {selectedClub && (
        <div className="mt-2 rounded-md bg-brand-50 px-3 py-2 text-sm text-brand-700">
          Selected: <strong>{selectedClub.name}</strong>
        </div>
      )}
    </div>
  );
}
